/**
 * Block 13 PR-G Phase 4A — Pure selectors над `ContractFormState`.
 *
 * Все функции pure (state → derived value), без side effects. Использовать в
 * View-layer для derived UI (Cost remaining, Spec total, button enable/disable).
 *
 * Reference:
 *   - Block 12 pattern: `features/calculator/anketa/state/selectors.ts`
 *   - PRD §10.13.2 «Оставшаяся сумма» = totalCost − advance
 */

import {
  resolveAdvanceAmount,
  type ContractRegion,
  type ContractSpecBlock,
} from '@pomoshnik/shared';

import type { ContractFormState } from './contractReducer';

/**
 * Phase 6.5G hotfix round 3 (R3-#1) — pure helper: sum row.cost across all
 * blocks. Extracted из selectTotalSpec для reuse в ContractCard (operates on
 * saved ContractDraft) и renderContractToPdf (operates on ContractDraft).
 * row.cost ?? 0 чтобы пропустить material rows без cost concept.
 */
export function sumSpecCost(spec: readonly ContractSpecBlock[]): number {
  return spec.reduce(
    (sum, block) => sum + block.rows.reduce((bSum, row) => bSum + (row.cost ?? 0), 0),
    0,
  );
}

/**
 * PR-D2 (ADR-0040) — эффективная сумма аванса. Когда ставка «Аванс, %»
 * (`state.advancePercent`) задана валидным числом 0–100 → производное
 * `round(selectTotalSpec × %/100)`, пересчитывается при изменении Суммы.
 * Иначе (null/legacy) → ручное значение `state.advance`. Единый источник
 * истины для UI (`SectionCostAndTerms`), остатка и PDF.
 */
export function selectAdvance(state: ContractFormState): number {
  return resolveAdvanceAmount(selectTotalSpec(state), state.advancePercent, state.advance);
}

/**
 * Остаток к доплате = selectTotalSpec(state) − эффективный аванс, clamped к ≥0.
 * Используется в Cost section (`SectionCostAndTerms`) как readonly hint.
 *
 * Phase 6.5G hotfix round 3 (R3-#1) — source of total switched from
 * `state.totalCost` (vestigial after read-only UI) to `selectTotalSpec(state)`.
 * PR-D2 (ADR-0040) — аванс через `selectAdvance` (учёт ставки «Аванс, %»).
 */
export function selectRemaining(state: ContractFormState): number {
  return Math.max(0, selectTotalSpec(state) - selectAdvance(state));
}

/**
 * Сумма всех позиций Спецификации (по всем блокам). Phase 4A → 0 (spec пуст);
 * Phase 4C → реальная сумма для отображения в «Итого» card SectionSpec.
 *
 * Phase 6.5B Amendment A2 (2026-05-24) — row.cost теперь optional (material
 * rows facade_material/corpus_material/hardware не имеют cost concept). Sum
 * use `row.cost ?? 0` чтобы пропустить такие rows.
 *
 * Phase 6.5G hotfix round 3 (R3-#1) — delegates к `sumSpecCost` (pure helper)
 * чтобы избежать дублирования логики между UI / PDF / ContractCard.
 */
export function selectTotalSpec(state: ContractFormState): number {
  return sumSpecCost(state.spec);
}

/**
 * Минимальная валидность для «Скачать PDF» / «Переслать клиенту» CTAs.
 * Не блокирует Сохранить — Сохранить разрешён в любом state (draft preserves
 * partial work).
 *
 * Critical fields (для PDF render):
 *   - customerName не пустой (договор без заказчика — bogus)
 *   - executorName не пустой
 *   - selectTotalSpec(state) > 0 (Phase 6.5G hotfix round 3 R3-#1 — source
 *     switched from state.totalCost, поскольку UI больше не диспатчит SET_FIELD
 *     totalCost; вместо этого сумма приходит из позиций Спецификации)
 *   - hydrationStatus === 'ready'
 *
 * **НЕ** требует contractNum/contractDate/contractCity — на их отсутствие
 * `renderContractToPdf` подставит placeholder '___'. Лучше не блокировать
 * пользователя из-за номера договора, который часто заполняется в последний
 * момент перед отправкой клиенту.
 */
export function selectIsDraftReadyForPdf(state: ContractFormState): boolean {
  return (
    state.hydrationStatus === 'ready' &&
    state.customerName.trim() !== '' &&
    state.executorName.trim() !== '' &&
    selectTotalSpec(state) > 0
  );
}

/**
 * Может ли пользователь нажать «Сохранить». Hydration ready + ≥1 поле
 * заполнено (минимум customerName, executorName, contractNum, либо позиция
 * Спецификации с cost > 0). Полностью пустой draft сохранять бессмысленно.
 *
 * Phase 6.5G hotfix round 3 (R3-#1) — добавлен `selectTotalSpec(state) > 0`
 * fallback (заменил `state.totalCost > 0` vestigial branch) чтобы user мог
 * сохранить draft с заполненной только Спецификацией без других полей.
 */
export function selectCanSaveContract(state: ContractFormState): boolean {
  if (state.hydrationStatus !== 'ready') return false;
  return (
    state.customerName.trim() !== '' ||
    state.executorName.trim() !== '' ||
    state.contractNum.trim() !== '' ||
    selectTotalSpec(state) > 0
  );
}

/**
 * Phase 6.5 #14 — какие секции мешают enable PDF / Share кнопок.
 * Возвращает упорядоченный массив labels (стабильный порядок для UX
 * consistency); пустой массив означает «всё заполнено, gates пройдены».
 *
 * Содержание mapping'а — mirror selectIsDraftReadyForPdf required fields:
 *   - 'Стороны' → если customerName OR executorName пуст
 *   - 'Стоимость' → если selectTotalSpec(state) === 0 (Phase 6.5G hotfix
 *     round 3 R3-#1 — source switched от state.totalCost; Сумма теперь
 *     read-only из Спецификации, но label «Стоимость» preserved поскольку
 *     визуально это та же секция с badge)
 *
 * Hydration === 'ready' check НЕ включён — UI BottomActions не показывает
 * hint до hydration (skeleton phase). Если потребуется, расширим.
 */
export function selectMissingSectionsForPdf(state: ContractFormState): string[] {
  const missing: string[] = [];
  if (state.customerName.trim() === '' || state.executorName.trim() === '') {
    missing.push('Стороны');
  }
  if (selectTotalSpec(state) <= 0) {
    missing.push('Стоимость');
  }
  return missing;
}

/**
 * Phase 6.5 #14 helper — true если секция «Стороны» имеет хоть одно пустое
 * required поле (customerName или executorName). Используется SectionParties
 * для рендера MissingFieldsBadge.
 */
export function selectPartiesSectionMissing(state: ContractFormState): boolean {
  return state.customerName.trim() === '' || state.executorName.trim() === '';
}

/**
 * Phase 6.5 #14 helper — true если секция «Стоимость и расчёты» имеет
 * пустое required поле (Сумма === 0). Используется SectionCostAndTerms
 * для рендера MissingFieldsBadge.
 *
 * Phase 6.5G hotfix round 3 (R3-#1) — source switched от state.totalCost
 * к selectTotalSpec(state) (Сумма теперь read-only из Спецификации).
 */
export function selectCostSectionMissing(state: ContractFormState): boolean {
  return selectTotalSpec(state) <= 0;
}

/**
 * Phase 6.5E Item H — derive `ContractRegion` из `templateKey`. После того
 * как user может менять region через RegionPicker, источник истины для
 * региона — это `state.templateKey` (а не `useAuthStore().region` из profile).
 * Callers: RegionPicker (current value), LanguagePicker (available languages
 * для нового региона), SectionPreamble (caskade dispatch).
 *
 * Все 5 templateKey формы имеют вид «{region}.{lang}», region всегда первая
 * часть. Fallback 'kz' нужен только теоретически — Zod гарантирует formed key.
 */
export function selectContractRegion(state: ContractFormState): ContractRegion {
  return (state.templateKey.split('.')[0] ?? 'kz') as ContractRegion;
}
